import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import ConfirmModal from '../shared/ConfirmModal';
import { formatMoney } from '../../utils/formatMoney';
import { athensYmd } from '../../utils/athensDate';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField } from '../settings/sheet';
import {
  ASIDE_BTN, Column, DataTable, DetailPanel, EmptyState, GHOST_BTN, ICON_BTN, INPUT_CLS, KeyValue, ListToolbar, Notice,
  PRIMARY_BTN, SELECT_CLS, Stat, Stats, matchesSearch,
} from '../shared/listPage';
import { BlockTitle, fmtDate, fmtDateTime } from './warehouseUi';

/**
 * Αποθήκη → «Αγορές» (14/10/2026): το ιστορικό αγορών σε πίνακα (κλικ = λεπτομέρειες στο συρτάρι) και η
 * «Νέα αγορά» σε συρτάρι — ως 14/10 φόρμα και ιστορικό εναλλάσσονταν στην ίδια θέση με ένα κουμπί. Ίδια
 * αιτήματα με πριν: προμηθευτές, πρώτες ύλες (όλες ή του προμηθευτή), εμπορεύματα από /api/products,
 * ιστορικό από /purchase-transactions, και POST /record-purchase με το ίδιο σώμα (και τις ίδιες σημειώσεις
 * ελλείψεων). Μετά την καταγραφή η φόρμα καθαρίζει όπως πριν και το συρτάρι κλείνει: η νέα αγορά φαίνεται
 * στην κορυφή του ιστορικού.
 */

interface Supplier {
  id: number;
  name: string;
  contact_person: string;
  phone: string;
  email: string;
}

interface RawMaterial {
  id: number;
  name: string;
  purchase_unit: string;
  purchase_price: number;
  current_stock: number;
  min_stock_level: number;
}

interface MerchandiseProduct {
  id: number;
  name: string;
  product_type: string;
  purchase_unit?: string;
  purchase_price?: number;
  current_stock?: number;
  min_stock_level?: number;
  supplier_id?: number;
}

interface PurchaseItem {
  raw_material_id: number;
  ordered_quantity: string; // '' = not set, otherwise numeric string
  quantity: number;
  unit_price: number;
  total_price: number;
  price_auto_filled: boolean;
}

interface PurchaseTransaction {
  id: number;
  supplier_id: number;
  supplier_name: string;
  invoice_number: string;
  purchase_date: string;
  total_amount: number;
  items: {
    material_name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    total_price: number;
  }[];
  notes: string;
  created_at: string;
  created_by: string;
}

interface PurchaseRecordingProps {
  /** Άνοιγμα με τη φόρμα «Νέα αγορά» (από την «Καταγραφή αγοράς» της κεφαλίδας). */
  startWithNew?: boolean;
}

// Σήμερα ΩΡΑΣ ΕΛΛΑΔΑΣ. Ως 24/10/2026 ήταν η ημερομηνία UTC: μια παραλαβή στις 00:30 γραφόταν με τη χθεσινή.
const todayIso = () => athensYmd();

const PurchaseRecording: React.FC<PurchaseRecordingProps> = ({ startWithNew = false }) => {
  const { t } = useTranslation(['warehouse', 'common', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const formId = `${uid}-form`;
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [merchandiseProducts, setMerchandiseProducts] = useState<MerchandiseProduct[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([]);
  const [showForm, setShowForm] = useState(startWithNew);
  const [transactions, setTransactions] = useState<PurchaseTransaction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PurchaseTransaction | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Fetch suppliers
  useEffect(() => {
    if (store?.id) {
      fetchSuppliers();
      fetchMaterials(); // Fetch all materials initially
      fetchMerchandiseProducts(); // Fetch merchandise products
      fetchTransactionHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/suppliers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchMaterials = async (supplierId?: number) => {
    try {
      // If supplier is selected, fetch materials for that supplier
      const url = supplierId
        ? `${API_BASE_URL}/api/warehouse/stores/${store?.id}/raw-materials?supplier_id=${supplierId}`
        : `${API_BASE_URL}/api/warehouse/stores/${store?.id}/raw-materials`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // The API returns rawMaterials, not materials
        setMaterials(data.rawMaterials || data.materials || []);
      }
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    }
  };

  const fetchMerchandiseProducts = async (supplierId?: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter only ΕΜΠΟΡΕΥΜΑ products, optionally by supplier
        let merchandise = data.products?.filter((p: MerchandiseProduct) => p.product_type === 'ΕΜΠΟΡΕΥΜΑ') || [];

        if (supplierId) {
          merchandise = merchandise.filter((p: MerchandiseProduct) => p.supplier_id === supplierId);
        }

        setMerchandiseProducts(merchandise);
      }
    } catch (error) {
      console.error('Failed to fetch merchandise products:', error);
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/purchase-transactions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setHistoryFailed(false);
      } else {
        setHistoryFailed(true);
      }
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      setHistoryFailed(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const retryHistory = () => {
    setHistoryLoading(true);
    fetchTransactionHistory();
  };

  const addPurchaseItem = () => {
    if (materials.length === 0 && merchandiseProducts.length === 0) {
      showToast('error', t('warehouse:addMaterialsFirst'));
      return;
    }
    setFormError(null);
    setPurchaseItems([...purchaseItems, {
      raw_material_id: 0,
      ordered_quantity: '',
      quantity: 0,
      unit_price: 0,
      total_price: 0,
      price_auto_filled: false,
    }]);
  };

  const updatePurchaseItem = (index: number, field: keyof PurchaseItem, value: any) => {
    const updated = [...purchaseItems];
    updated[index] = { ...updated[index], [field]: value };

    // When material changes, auto-fill last purchase price
    if (field === 'raw_material_id') {
      const mat = materials.find(m => m.id === Number(value));
      if (mat?.purchase_price && mat.purchase_price > 0) {
        updated[index].unit_price = mat.purchase_price;
        updated[index].price_auto_filled = true;
        updated[index].total_price = updated[index].quantity * mat.purchase_price;
      } else {
        updated[index].price_auto_filled = false;
      }
    }

    // Recalculate total
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total_price = updated[index].quantity * updated[index].unit_price;
    }
    // Clear auto-fill flag if user manually edited price
    if (field === 'unit_price') {
      updated[index].price_auto_filled = false;
    }

    setFormError(null);
    setPurchaseItems(updated);
  };

  const getShortage = (item: PurchaseItem): number | null => {
    const ordered = parseFloat(item.ordered_quantity);
    if (!item.ordered_quantity || isNaN(ordered)) return null;
    const diff = ordered - item.quantity;
    return diff > 0.001 ? diff : null;
  };

  const removePurchaseItem = (index: number) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return purchaseItems.reduce((sum, item) => sum + item.total_price, 0);
  };

  const resetForm = () => {
    setSelectedSupplier(null);
    setInvoiceNumber('');
    setPurchaseDate(todayIso());
    setNotes('');
    setPurchaseItems([]);
    setFormError(null);
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (loading) return;
    if (!selectedSupplier || purchaseItems.length === 0) {
      setFormError(t('warehouse:selectSupplierAndItem'));
      return;
    }

    setLoading(true);
    try {
      // Build shortage note if any items have partial delivery
      const shortageLines = purchaseItems
        .filter(i => getShortage(i) !== null)
        .map(i => {
          const mat = materials.find(m => m.id === i.raw_material_id);
          const shortage = getShortage(i)!;
          // Κείμενο που αποθηκεύεται στις σημειώσεις της αγοράς (δεδομένα, όχι οθόνη): ίδιο με πριν.
          return `${mat?.name || 'Υλικό'}: παρήγγειλα ${i.ordered_quantity} παρέλαβα ${i.quantity} ${mat?.purchase_unit || ''} (έλλειψη ${shortage.toFixed(3).replace(/\.?0+$/, '')})`;
        });
      const fullNotes = [notes, ...shortageLines].filter(Boolean).join(' | ');

      const response = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/record-purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          supplier_id: selectedSupplier,
          invoice_number: invoiceNumber,
          purchase_date: purchaseDate,
          items: purchaseItems,
          notes: fullNotes || null,
          total_amount: calculateTotal()
        })
      });

      if (response.ok) {
        // Reset form
        resetForm();
        setShowForm(false);
        showToast('success', t('warehouse:ui.purchases.recorded'));
        // Refresh data
        await fetchMaterials();
        await fetchTransactionHistory();
      } else {
        const error = await response.json();
        showToast('error', error.message || t('warehouse:purchaseRecordFailed'));
      }
    } catch (error) {
      console.error('Failed to record purchase:', error);
      showToast('error', t('warehouse:purchaseRecordFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Η φόρμα «έχει κάτι»: κλείσιμο με ερώτηση, για να μη χαθεί ένα τιμολόγιο από ένα κλικ έξω.
  const formTouched = selectedSupplier !== null || invoiceNumber !== '' || notes !== '' || purchaseItems.length > 0
    || purchaseDate !== todayIso();

  const closeForm = () => {
    setConfirmDiscard(false);
    setShowForm(false);
  };

  const requestCloseForm = () => {
    if (confirmDiscard) {
      setConfirmDiscard(false);
      return;
    }
    if (loading) return;
    if (formTouched) setConfirmDiscard(true);
    else closeForm();
  };

  const discardForm = () => {
    resetForm();
    closeForm();
  };

  const openNew = () => {
    setSelectedTransaction(null);
    setFormError(null);
    setShowForm(true);
  };

  const filteredTransactions = transactions.filter(tx => matchesSearch(searchTerm, tx.supplier_name, tx.invoice_number));

  const shortageItems = purchaseItems.filter(i => getShortage(i) !== null);

  const columns: Column<PurchaseTransaction>[] = [
    {
      key: 'purchase_date',
      header: t('warehouse:ui.purchases.date'),
      width: '130px',
      render: tx => <span className="tabular-nums">{fmtDate(tx.purchase_date)}</span>,
      stackRender: tx => fmtDate(tx.purchase_date),
    },
    {
      key: 'supplier_name',
      header: t('warehouse:ui.purchases.supplier'),
      width: 'minmax(170px, 2fr)',
      stack: 'title',
      render: tx => <span className="font-medium">{tx.supplier_name}</span>,
      stackRender: tx => tx.supplier_name,
    },
    {
      key: 'invoice_number',
      header: t('warehouse:ui.purchases.invoice'),
      width: 'minmax(130px, 1fr)',
      render: tx => (tx.invoice_number
        ? <span className="tabular-nums text-t2">{tx.invoice_number}</span>
        : <span className="text-t3">{t('warehouse:ui.purchases.noInvoice')}</span>),
      stackRender: tx => tx.invoice_number || t('warehouse:ui.purchases.noInvoice'),
    },
    {
      key: 'items',
      header: t('warehouse:ui.purchases.items'),
      width: '100px',
      align: 'right',
      hideBelow: 'medium',
      render: tx => <span className="tabular-nums text-t2">{(tx.items || []).length}</span>,
    },
    {
      key: 'total_amount',
      header: t('warehouse:ui.purchases.total'),
      width: '130px',
      align: 'right',
      stack: 'end',
      render: tx => <span className="font-medium">{formatMoney(Number(tx.total_amount))}</span>,
      stackRender: tx => formatMoney(Number(tx.total_amount)),
    },
    {
      key: 'created_at',
      header: t('warehouse:ui.purchases.recordedAt'),
      width: '170px',
      hideBelow: 'medium',
      render: tx => <span className="text-xs text-t3 tabular-nums">{fmtDateTime(tx.created_at)}</span>,
    },
  ];

  const fid = (name: string) => `${uid}-${name}`;

  const renderItem = (item: PurchaseItem, index: number) => {
    const shortage = getShortage(item);
    const mat = materials.find(m => m.id === item.raw_material_id);
    const shortageText = shortage !== null ? `-${shortage.toFixed(3).replace(/\.?0+$/, '')} ${mat?.purchase_unit || ''}`.trim() : '';
    return (
      // eslint-disable-next-line react/no-array-index-key -- τα είδη δεν έχουν id (ίδιο κλειδί με πριν)
      <li key={index} className="py-4 first:pt-0" data-testid={`purchase-item-${index}`}>
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <label htmlFor={fid(`i${index}-material`)} className="block text-xs text-t3 mb-1">{t('warehouse:ui.purchases.material')}</label>
            <select
              id={fid(`i${index}-material`)}
              value={item.raw_material_id}
              onChange={e => updatePurchaseItem(index, 'raw_material_id', Number(e.target.value))}
              className={SELECT_CLS}
            >
              <option value={0}>
                {materials.length === 0 && merchandiseProducts.length === 0
                  ? t('warehouse:noAvailableItems')
                  : t('warehouse:selectMaterialOrProduct')}
              </option>
              {materials.length > 0 && (
                <optgroup label={t('warehouse:rawMaterials')}>
                  {materials.map(material => (
                    <option key={`mat-${material.id}`} value={material.id}>
                      {material.name} ({material.purchase_unit}) - {t('warehouse:stockLabel')}: {material.current_stock}
                    </option>
                  ))}
                </optgroup>
              )}
              {merchandiseProducts.length > 0 && (
                <optgroup label={t('warehouse:merchandise')}>
                  {merchandiseProducts.map(product => (
                    <option key={`prod-${product.id}`} value={product.id}>
                      {product.name} - {t('warehouse:stockLabel')}: {product.current_stock || 0} - {formatMoney(Number(product.purchase_price || 0))}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <button
            type="button"
            onClick={() => removePurchaseItem(index)}
            aria-label={t('warehouse:ui.purchases.removeItem')}
            title={t('warehouse:ui.purchases.removeItem')}
            className={`${ICON_BTN} hover:text-red-600 dark:hover:text-red-400`}
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label htmlFor={fid(`i${index}-ordered`)} className="block text-xs text-t3 mb-1">
              {t('warehouse:orderedQty')}
              <span className="ml-1 text-t4">{t('warehouse:optionalShort')}</span>
            </label>
            <input
              id={fid(`i${index}-ordered`)}
              type="number"
              value={item.ordered_quantity}
              onChange={e => updatePurchaseItem(index, 'ordered_quantity', e.target.value)}
              placeholder="—"
              min="0"
              step="0.001"
              className={`${INPUT_CLS} tabular-nums`}
            />
          </div>
          <div>
            <label htmlFor={fid(`i${index}-received`)} className="block text-xs text-t3 mb-1">
              {t('warehouse:receivedQty')}
              {shortage !== null && <span className="ml-1.5 font-semibold text-amber-800 dark:text-amber-300">{shortageText}</span>}
            </label>
            <input
              id={fid(`i${index}-received`)}
              type="number"
              value={item.quantity}
              onChange={e => updatePurchaseItem(index, 'quantity', Number(e.target.value))}
              placeholder="0"
              min="0"
              step="0.001"
              aria-invalid={shortage !== null || undefined}
              className={`${INPUT_CLS} tabular-nums ${shortage !== null ? 'border-amber-500' : ''}`}
            />
          </div>
          <div>
            <label htmlFor={fid(`i${index}-price`)} className="block text-xs text-t3 mb-1">
              {t('warehouse:unitPriceEuro')}
              {item.price_auto_filled && <span className="ml-1.5 text-emerald-700 dark:text-emerald-400">{t('warehouse:lastPrice')}</span>}
            </label>
            <input
              id={fid(`i${index}-price`)}
              type="number"
              value={item.unit_price}
              onChange={e => updatePurchaseItem(index, 'unit_price', Number(e.target.value))}
              placeholder="0.00"
              min="0"
              step="0.01"
              className={`${INPUT_CLS} tabular-nums`}
            />
          </div>
          <div>
            <span className="block text-xs text-t3 mb-1">{t('warehouse:totalEuro')}</span>
            <span className="flex items-center min-h-[44px] text-base font-semibold text-t1 tabular-nums" data-testid={`purchase-item-${index}-total`}>
              {formatMoney(Number(item.total_price))}
            </span>
          </div>
        </div>
      </li>
    );
  };

  const renderForm = () => (
    <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="purchase-form">
      {/* Supplier and Invoice Info */}
      <div className="xs-grid2">
        <SheetField label={t('warehouse:supplierRequired')} htmlFor={fid('supplier')}>
          <select
            id={fid('supplier')}
            value={selectedSupplier || ''}
            onChange={e => {
              const supplierId = Number(e.target.value);
              setFormError(null);
              setSelectedSupplier(supplierId);
              // Fetch materials and merchandise for this supplier
              if (supplierId) {
                fetchMaterials(supplierId);
                fetchMerchandiseProducts(supplierId);
              }
            }}
            className={SELECT_CLS}
          >
            <option value="">{t('warehouse:selectSupplier')}</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </SheetField>
        <SheetField label={t('warehouse:ui.purchases.invoiceNumber')} htmlFor={fid('invoice')}>
          <input
            id={fid('invoice')}
            type="text"
            value={invoiceNumber}
            onChange={e => setInvoiceNumber(e.target.value)}
            placeholder="INV-2024-001"
            autoComplete="off"
            className={`${INPUT_CLS} tabular-nums`}
          />
        </SheetField>
        <SheetField label={t('warehouse:ui.purchases.purchaseDate')} htmlFor={fid('date')}>
          <input id={fid('date')} type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} className={INPUT_CLS} />
        </SheetField>
        <SheetField label={t('common:notes')} htmlFor={fid('notes')}>
          <textarea
            id={fid('notes')}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('warehouse:additionalNotes')}
            rows={2}
            className={`${INPUT_CLS} resize-y`}
          />
        </SheetField>
      </div>

      {/* Purchase Items */}
      <div role="group" aria-labelledby={fid('items-title')}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pt-5 border-t border-line">
          <BlockTitle id={fid('items-title')}>{t('warehouse:ui.purchases.itemsTitle')}</BlockTitle>
          <button type="button" onClick={addPurchaseItem} className={ASIDE_BTN} disabled={materials.length === 0}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('warehouse:ui.purchases.addItem')}
          </button>
        </div>
        {materials.length === 0 && (
          <div className="mb-3"><Notice>{t('warehouse:noAvailableMaterials')}</Notice></div>
        )}

        {purchaseItems.length === 0 ? (
          <div className="py-6 text-center border border-dashed border-line-strong rounded-xl">
            <p className="text-sm text-t2">{t('warehouse:noItemsAdded')}</p>
            <p className="mt-1 text-xs text-t3">{t('warehouse:ui.purchases.noItemsHint')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-line" data-testid="purchase-items">
            {purchaseItems.map(renderItem)}
          </ul>
        )}
      </div>

      {/* Shortage summary */}
      {shortageItems.length > 0 && (
        <Notice testId="purchase-shortages">
          <span className="font-semibold">{t('warehouse:shortages')}</span>{' '}
          {shortageItems.map((item, idx) => {
            const mat = materials.find(m => m.id === item.raw_material_id);
            const shortage = getShortage(item)!;
            return (
              // eslint-disable-next-line react/no-array-index-key -- όπως πριν
              <span key={idx}>
                {idx > 0 && ' · '}
                {mat?.name || '—'} <strong>-{shortage.toFixed(3).replace(/\.?0+$/, '')}</strong> {mat?.purchase_unit || ''}
              </span>
            );
          })}
        </Notice>
      )}
    </form>
  );

  const detailItems = (tx: PurchaseTransaction) => [
    { id: 'supplier', label: t('warehouse:ui.purchases.supplier'), value: tx.supplier_name },
    { id: 'invoice', label: t('warehouse:ui.purchases.invoice'), value: tx.invoice_number || t('warehouse:ui.purchases.noInvoice') },
    { id: 'date', label: t('warehouse:ui.purchases.date'), value: fmtDate(tx.purchase_date) },
    { id: 'total', label: t('warehouse:ui.purchases.total'), value: formatMoney(Number(tx.total_amount)) },
    { id: 'recorded', label: t('warehouse:ui.purchases.recordedAt'), value: fmtDateTime(tx.created_at) },
  ];

  return (
    <>
      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('warehouse:searchBySupplierOrInvoice'),
          ariaLabel: t('warehouse:ui.purchases.searchLabel'),
        }}
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={openNew}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('warehouse:ui.purchases.new')}
          </button>
        )}
        summary={!historyLoading && !historyFailed ? t('warehouse:ui.purchases.count', { count: filteredTransactions.length }) : null}
      />

      {historyFailed ? (
        <LoadFailed message={t('warehouse:ui.purchases.loadFailed')} onRetry={retryHistory} />
      ) : (
        <DataTable
          columns={columns}
          rows={filteredTransactions}
          rowKey={tx => tx.id}
          onRowClick={setSelectedTransaction}
          selectedKey={selectedTransaction?.id ?? null}
          loading={historyLoading}
          stickyHeader
          ariaLabel={t('warehouse:purchaseHistory')}
          rowLabel={tx => t('warehouse:ui.purchases.rowLabel', { supplier: tx.supplier_name, date: fmtDate(tx.purchase_date) })}
          testId="purchases-table"
          empty={searchTerm.trim()
            ? <EmptyState title={t('warehouse:noPurchaseHistory')} hint={t('warehouse:ui.tryAnotherSearch')} />
            : <EmptyState icon={History} title={t('warehouse:noPurchaseHistory')} hint={t('warehouse:ui.purchases.emptyHint')} />}
        />
      )}

      {/* Λεπτομέρειες αγοράς */}
      <DetailPanel
        open={selectedTransaction !== null}
        onClose={() => setSelectedTransaction(null)}
        title={selectedTransaction?.supplier_name || ''}
        subtitle={selectedTransaction ? `${fmtDate(selectedTransaction.purchase_date)} · ${selectedTransaction.invoice_number || t('warehouse:ui.purchases.noInvoice')}` : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelectedTransaction(null)}>{t('listPage:close')}</button>}
        testId="purchase-detail"
      >
        {selectedTransaction && (
          <div className="space-y-6">
            <KeyValue items={detailItems(selectedTransaction)} testId="purchase-detail-values" />
            <div>
              <BlockTitle className="mb-2">{t('warehouse:ui.purchases.itemsHeading', { count: (selectedTransaction.items || []).length })}</BlockTitle>
              <ul className="divide-y divide-line" data-testid="purchase-detail-items">
                {(selectedTransaction.items || []).map((item, idx) => (
                  // eslint-disable-next-line react/no-array-index-key -- τα είδη του ιστορικού δεν έχουν id
                  <li key={idx} className="py-2.5 flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 text-t1">{item.material_name}</span>
                    <span className="shrink-0 text-t3 tabular-nums">
                      {t('warehouse:ui.purchases.itemLine', {
                        quantity: Number(item.quantity),
                        unit: item.unit,
                        price: formatMoney(Number(item.unit_price)),
                        total: formatMoney(Number(item.total_price)),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {selectedTransaction.notes && (
              <div>
                <BlockTitle className="mb-1">{t('common:notes')}</BlockTitle>
                <p className="text-sm text-t2 whitespace-pre-line">{selectedTransaction.notes}</p>
              </div>
            )}
          </div>
        )}
      </DetailPanel>

      {/* Νέα αγορά */}
      <DetailPanel
        open={showForm}
        onClose={requestCloseForm}
        title={t('warehouse:ui.purchases.new')}
        subtitle={t('warehouse:recordNewPurchaseDesc')}
        width="lg"
        footerStart={purchaseItems.length > 0 ? (
          <Stats testId="purchase-total">
            <Stat label={t('warehouse:ui.purchases.total')} value={formatMoney(calculateTotal())} />
          </Stats>
        ) : undefined}
        footer={(
          <>
            {formError && <p role="alert" className="basis-full text-sm font-medium text-red-600 dark:text-red-400 text-right">{formError}</p>}
            <button type="button" className={GHOST_BTN} onClick={requestCloseForm} disabled={loading}>{t('warehouse:cancelShort')}</button>
            <button type="submit" form={formId} className={PRIMARY_BTN} disabled={loading} aria-busy={loading || undefined}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {loading ? t('warehouse:recording') : t('warehouse:ui.purchases.submit')}
            </button>
          </>
        )}
        testId="purchase-panel"
      >
        {showForm && renderForm()}
        <ConfirmModal
          isOpen={confirmDiscard}
          title={t('warehouse:ui.purchases.discardTitle')}
          message={t('warehouse:ui.purchases.discardMessage')}
          confirmLabel={t('warehouse:ui.discard')}
          cancelLabel={t('warehouse:ui.keepEditing')}
          variant="warning"
          onConfirm={discardForm}
          onCancel={() => setConfirmDiscard(false)}
        />
      </DetailPanel>
    </>
  );
};

export default PurchaseRecording;
